import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Toaster } from '@/components/ui/sonner';
import { ChatWidget } from '@/components/chat/ChatWidget';
import { ImpersonationBanner } from '@/components/ImpersonationBanner';
import { SectionBoundary } from '@/components/ui/section-error';
import * as Sentry from "@sentry/react";

const HomePage          = lazy(() => import('@/pages/HomePage').then(m => ({ default: m.HomePage })));
const MenuPage          = lazy(() => import('@/pages/MenuPage').then(m => ({ default: m.MenuPage })));
const OrderFunnelPage   = lazy(() => import('@/pages/OrderFunnelPage').then(m => ({ default: m.OrderFunnelPage })));
const PlanFinderPage    = lazy(() => import('@/pages/PlanFinderPage').then(m => ({ default: m.PlanFinderPage })));
const BlogPage          = lazy(() => import('@/pages/BlogPage').then(m => ({ default: m.BlogPage })));
const BlogPostPage      = lazy(() => import('@/pages/BlogPostPage').then(m => ({ default: m.BlogPostPage })));
const AdminDashboard    = lazy(() => import('@/pages/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const CheckoutCancelPage = lazy(() => import('./pages/CheckoutCancelPage').then(m => ({ default: m.CheckoutCancelPage })));
const CheckoutReviewPage = lazy(() => import('@/pages/CheckoutReviewPage').then(m => ({ default: m.CheckoutReviewPage })));
const MealSelectionPage  = lazy(() => import('@/pages/MealSelectionPage').then(m => ({ default: m.MealSelectionPage })));
const AccountPage        = lazy(() => import('@/pages/AccountPage').then(m => ({ default: m.AccountPage })));
const PlansPage          = lazy(() => import('@/pages/PlansPage').then(m => ({ default: m.PlansPage })));
const NotFoundPage            = lazy(() => import('@/pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })));
const AffiliateLandingPage    = lazy(() => import('@/pages/AffiliateLandingPage').then(m => ({ default: m.AffiliateLandingPage })));
const AffiliatePortalPage     = lazy(() => import('@/pages/AffiliatePortalPage').then(m => ({ default: m.AffiliatePortalPage })));
const EditMealsPage           = lazy(() => import('@/pages/EditMealsPage').then(m => ({ default: m.EditMealsPage })));
const SwitchPlanPage          = lazy(() => import('@/pages/SwitchPlanPage').then(m => ({ default: m.SwitchPlanPage })));
const SwitchGoalPage          = lazy(() => import('@/pages/SwitchGoalPage').then(m => ({ default: m.SwitchGoalPage })));
const TermsPage               = lazy(() => import('@/pages/TermsPage').then(m => ({ default: m.TermsPage })));
const ShippingPolicyPage      = lazy(() => import('@/pages/ShippingPolicyPage').then(m => ({ default: m.ShippingPolicyPage })));
const WaitlistPage            = lazy(() => import('@/pages/WaitlistPage').then(m => ({ default: m.WaitlistPage })));
const FAQPage                 = lazy(() => import('@/pages/FAQPage').then(m => ({ default: m.FAQPage })));

function AppContent() {
  const location = useLocation();
  const { impersonation } = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');
    if (ref) {
      const existingRef = document.cookie.split('; ').find(r => r.startsWith('pm_ref='))?.split('=')[1];
      const isNewClick = !existingRef || decodeURIComponent(existingRef) !== ref;
      document.cookie = `pm_ref=${encodeURIComponent(ref)}; Path=/; Max-Age=2592000; SameSite=Lax`;
      if (isNewClick) {
        supabase.functions.invoke('track-affiliate-click', { body: { ref_code: ref } }).catch(() => {});
      }
    }
    const customerRef = params.get('pm_customer_ref');
    if (customerRef) {
      document.cookie = `pm_customer_ref=${encodeURIComponent(customerRef)}; Path=/; Max-Age=2592000; SameSite=Lax`;
    }
  }, [location.search]);

  const hideChrome = location.pathname.startsWith('/admin') || location.pathname === '/select-meals' || location.pathname === '/account/edit-meals' || location.pathname === '/checkout-review' || location.pathname === '/affiliate/portal';

  return (
    <>
      <ImpersonationBanner />
      {impersonation.active && <div className="h-10" />}
      {!hideChrome && <Navbar />}
      <Suspense fallback={<div className="min-h-screen" />}>
        {/* Route-level boundary: a render crash inside a page shows a
            recoverable fallback with the navbar still alive instead of
            blanking the whole app via the top-level Sentry boundary.
            key remounts the boundary on navigation, so leaving a crashed
            page automatically clears the error state. */}
        <main className={!hideChrome ? 'pt-24' : undefined}>
        <SectionBoundary key={location.pathname} label="This page">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/menu" element={<MenuPage />} />
          <Route path="/plans" element={<OrderFunnelPage />} />
          <Route path="/select-meals" element={<MealSelectionPage />} />
          <Route path="/plan-finder" element={<PlanFinderPage />} />
          <Route path="/all-plans" element={<PlansPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:id" element={<BlogPostPage />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/checkout-review" element={<CheckoutReviewPage />} />
          <Route path="/checkout-cancel"  element={<CheckoutCancelPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/account/edit-meals" element={<EditMealsPage />} />
          <Route path="/switch-plan" element={<SwitchPlanPage />} />
          <Route path="/switch-goal" element={<SwitchGoalPage />} />
          <Route path="/affiliate" element={<AffiliateLandingPage />} />
          <Route path="/affiliate/portal" element={<AffiliatePortalPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/shipping-policy" element={<ShippingPolicyPage />} />
          <Route path="/waitlist" element={<WaitlistPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </SectionBoundary>
        </main>
        {!hideChrome && <Footer />}
      </Suspense>
      <Toaster position="bottom-right" />
      <ChatWidget hideChrome={hideChrome} />
    </>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={<p>Something went wrong.</p>}>
      <HelmetProvider>
        <AuthProvider>
          <Router>
            <div className="min-h-screen bg-white">
              <AppContent />
            </div>
          </Router>
        </AuthProvider>
      </HelmetProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
